import { Link, useParams, useSearchParams } from "react-router";
import { useLocale, useTranslations } from "use-intl";
import { BarChart3, Settings2 } from "lucide-react";
import { ll, lt } from "@liftquest/shared/localized";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { NotFound, PageLoading } from "@/components/app/states";
import { ExerciseVideo } from "@/components/app/video";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useExercise } from "@/lib/queries";
import { ExerciseForm } from "./exercise-form";

export function NewExercisePage() {
  const [params] = useSearchParams();
  const t = useTranslations("exercises");
  const returnTo = params.get("returnTo") ?? undefined;
  return (
    <Page>
      <PageHeader backHref={returnTo?.startsWith("/") ? returnTo : "/exercises"} title={t("new")} />
      <ExerciseForm returnTo={returnTo} />
    </Page>
  );
}

export function ExerciseDetailPage() {
  const { exerciseId = "" } = useParams();
  const locale = useLocale();
  const t = useTranslations("exercises");
  const tc = useTranslations("common");
  const tm = useTranslations("muscles");
  const te = useTranslations("equipment");
  const tp = useTranslations("progress");
  const query = useExercise(exerciseId);
  if (query.isPending) return <PageLoading />;
  if (!query.data) return <NotFound />;
  const { exercise: ex, usage } = query.data;
  const name = lt(ex.name, locale);
  const steps = ll(ex.instructions, locale);
  const setup = lt(ex.setup, locale);

  return (
    <Page>
      <PageHeader backHref="/exercises" title={name} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge>{te.has(ex.equipment as "machine") ? te(ex.equipment as "machine") : ex.equipment}</Badge>
        {ex.muscles.map((m) => (
          <Badge key={m} variant="secondary">
            {tm.has(m as "abs") ? tm(m as "abs") : m}
          </Badge>
        ))}
      </div>
      <Tabs defaultValue="view" className="mt-4">
        <TabsList className="w-full">
          <TabsTrigger value="view">{t("instructions")}</TabsTrigger>
          <TabsTrigger value="edit">{tc("edit")}</TabsTrigger>
        </TabsList>
        <TabsContent value="view">
          <div className="mt-3">
            <ExerciseVideo url={ex.videoUrl} title={name} />
          </div>
          {setup && (
            <Section title={t("setup")}>
              <p className="flex gap-2 text-sm">
                <Settings2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                {setup}
              </p>
            </Section>
          )}
          {steps.length > 0 && (
            <Section title={t("instructions")}>
              <ol className="space-y-2">
                {steps.map((s, i) => (
                  <li key={s} className="flex gap-3 text-sm">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {usage.length > 0 && (
            <Section title={t("usedIn")}>
              <ul className="flex flex-wrap gap-1.5">
                {usage.map((u) => (
                  <li key={u.id}>
                    <Link to={`/plans/${u.planId}/workouts/${u.workoutId}`}>
                      <Badge variant="outline">
                        {lt(u.planName, locale)} · {lt(u.workoutName, locale)}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <Button asChild variant="outline" className="mt-6 w-full">
            <Link to={`/progress/exercise/${ex.id}`}>
              <BarChart3 /> {tp("exerciseProgress")}
            </Link>
          </Button>
        </TabsContent>
        <TabsContent value="edit">
          <ExerciseForm
            key={ex.updatedAt}
            id={ex.id}
            initial={{
              name: ex.name,
              type: ex.type,
              muscles: ex.muscles,
              equipment: ex.equipment,
              videoUrl: ex.videoUrl ?? "",
              instructions: ex.instructions,
              setup: ex.setup,
              weightStep: ex.weightStep,
            }}
          />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
